// Only the auth-state reader may supply a server-confirmed identity. Browser markers revoke
// access across tabs; they never select or authenticate a different account.
import { setPrivateAuthority } from '@/offline/privateStorage'
export type ActiveIdentity = { id: string; legacy_owner: boolean }
export const IDENTITY_HEADER = 'X-Telos-User-ID'
const REVISION_KEY = 'telos-identity-revision'
let active: ActiveIdentity | null = null
let revision: string | null = null
let channel: BroadcastChannel | null = null
// A tab may resume its own previously verified session offline. A browser-wide revision
// revokes this evidence after another tab logs out/switches; it never grants another user.
try {
  const saved = JSON.parse(sessionStorage.getItem('telos-verified-identity') ?? 'null') as
    { user: ActiveIdentity; revision: string } | null
  if (saved?.revision && saved.revision === localStorage.getItem(REVISION_KEY)) {
    active = saved.user; revision = saved.revision
  }
} catch { /* storage unavailable or no verified session */ }
let generation = 0
type IdentityListener = (reason?: 'revoked') => void
const listeners = new Set<IdentityListener>()
const cleaners = new Set<(owner: string | null) => void | Promise<void>>()
const initializers = new Set<(user: ActiveIdentity) => void | Promise<void>>()
type IdentityTransition = {
  user: ActiveIdentity | null
  version: number
  revision: string
  done: Promise<void>
}
let transition: IdentityTransition | null = null
const sameIdentity = (left: ActiveIdentity | null, right: ActiveIdentity | null) =>
  left?.id === right?.id && left?.legacy_owner === right?.legacy_owner
function browserRevision(): string | null {
  try { return localStorage.getItem(REVISION_KEY) } catch { return revision }
}
function emit(reason?: 'revoked') { for (const listener of listeners) listener(reason) }
export function subscribeIdentity(listener: IdentityListener): () => void {
  listeners.add(listener)
  return () => listeners.delete(listener)
}
export function registerIdentityStorage(
  clean: (owner: string | null) => void | Promise<void>,
  initialize?: (user: ActiveIdentity) => void | Promise<void>,
): void {
  cleaners.add(clean)
  if (initialize) initializers.add(initialize)
}
function revoke(): void {
  if (!active && !transition) return
  const previous = active?.id ?? transition?.user?.id ?? null
  active = null
  transition = null
  try { sessionStorage.removeItem('telos-verified-identity') } catch { /* memory only */ }
  generation++
  emit('revoked')
  void Promise.allSettled([...cleaners].map((clean) => Promise.resolve().then(() => clean(previous))))
}
export function getActiveUserId(): string | null {
  if ((active || transition) && browserRevision() !== revision) revoke()
  return active?.id ?? null
}
export function getIdentityKey(): string {
  const owner = getActiveUserId()
  return `${generation}:${owner ?? 'anonymous'}`
}
export function captureIdentity() {
  const id = getActiveUserId()
  const version = generation
  const isUnchanged = () => getActiveUserId() === id && generation === version
  return { id, isCurrent: () => id !== null && isUnchanged(), isUnchanged }
}
export async function clearIdentityCaches(): Promise<void> {
  const owner = getActiveUserId()
  generation++
  emit()
  await Promise.allSettled([...cleaners].map((clean) => clean(owner)))
}
export async function setActiveIdentity(user: ActiveIdentity | null): Promise<void> {
  // Re-entrant auth consumers can mount while the offline provider changes namespaces.
  // They must await the existing transition, including its cleanup, rather than restart it.
  getActiveUserId()
  const pending = transition?.version === generation && transition.revision === browserRevision() ? transition : null
  if (pending && sameIdentity(pending.user, user)) return pending.done
  if (!pending && sameIdentity(active, user) && revision === browserRevision()) return
  const previous = active?.id ?? null
  active = null
  const version = ++generation
  const stored = browserRevision()
  let sameOwner = false
  try { sameOwner = JSON.parse(stored ?? 'null')?.owner === (user?.id ?? null) } catch { /* old marker */ }
  revision = sameOwner && stored ? stored : JSON.stringify({ owner: user?.id ?? null, nonce: crypto.randomUUID() })
  const transitionRevision = revision
  const isCurrentTransition = () => generation === version && browserRevision() === transitionRevision
  try { localStorage.setItem(REVISION_KEY, revision) } catch { /* memory-only browser */ }
  channel?.postMessage({ owner: user?.id ?? null, revision })
  try { sessionStorage.removeItem('telos-verified-identity') } catch { /* memory-only browser */ }
  const next: IdentityTransition = {
    user: user ? { ...user } : null, version, revision,
    done: completeIdentityTransition(user, previous, isCurrentTransition),
  }
  transition = next
  emit()
  try { await next.done } finally { if (transition === next) transition = null }
}
async function completeIdentityTransition(
  user: ActiveIdentity | null,
  previous: string | null,
  isCurrentTransition: () => boolean,
): Promise<void> {
  await setPrivateAuthority(null, isCurrentTransition)
  await Promise.allSettled([...cleaners].map((clean) => clean(previous)))
  if (user) await Promise.allSettled([...initializers].map((initialize) => initialize(user)))
  if (!isCurrentTransition()) return
  active = user ? { ...user } : null
  try {
    if (active) sessionStorage.setItem('telos-verified-identity', JSON.stringify({ user: active, revision }))
  } catch { /* memory-only browser */ }
  await setPrivateAuthority(active?.id ?? null, isCurrentTransition)
  if (!isCurrentTransition()) return
  emit()
}
registerIdentityStorage(async (owner) => {
  if (typeof caches === 'undefined') return
  const keys = await caches.keys()
  await Promise.all(keys.filter((key) => key === 'api-data-v1' || key === 'share-inbox-v1' ||
    (owner && (key === `api-data-v2:${owner}` || key === `share-inbox-v2:${owner}`)))
    .map((key) => caches.delete(key)))
})
if (typeof window !== 'undefined') {
  if ('BroadcastChannel' in window) {
    channel = new window.BroadcastChannel('telos-identity')
    channel.onmessage = (event: MessageEvent<{ owner?: string | null }>) => {
      if (event.data.owner !== active?.id) revoke()
    }
  }
  window.addEventListener('storage', (event) => {
    if (event.key === null || event.key === REVISION_KEY) void getActiveUserId()
  })
  window.addEventListener('pageshow', () => { void getActiveUserId() })
  document.addEventListener('visibilitychange', () => { void getActiveUserId() })
}
